import { combineReducers, configureStore } from "@reduxjs/toolkit";
import solarProductDetailsReducer from "./slices/solar-product-details-slice";
import firstLoginReducer from "./slices/first-login-slice";
import loaderReducer from "./slices/loader-slice";
import currentUserReducer, { logOut, tokenExpired } from "./slices/current-user-slice";
import customerRegistrationReducer from "./slices/customer-registration-slice";
import solarRecommendationReducer from "./slices/solar-recommendation-slice";
import buySolarProductDetailsReducer from "./slices/buy-solar-product-slice";
import guestDelieveryAddressReducer from "./slices/guest-delivery-address-slice";
import signUpReducer from "./slices/sign-up-slice";
import billsLayoutReducer from './slices/bills-layout-slice';
import serviceTypeReducer from './slices/service-type-slice';
import energyProfileReducer from './slices/customer-energy-profile-slice';
import currentBuyElectricityTransactionReducer from './slices/buy-electricity-slice';
import bulkPurchaseReducer from './slices/bulk-purchase-slice';
import guestBillPaymentReducer from './slices/guest-bill-payment-slice';
import workflowReducer from "./slices/workflow-slice";
import getSolarReducer from "./slices/get-solar-slice";
import notificationReducer from "./slices/notification-slice";
import broadcastNotificationReducer from "./slices/broadcast-notification-slice";

import { apiSlice } from "./base/api-slice";

export type RootState = ReturnType<typeof appReducer>;

const appReducer = combineReducers({
	[apiSlice.reducerPath]: apiSlice.reducer,
	user: currentUserReducer,
	solarProductDetails: solarProductDetailsReducer,
	firstLogin: firstLoginReducer,
	signUp: signUpReducer,
	loader: loaderReducer,
	customerRegistration: customerRegistrationReducer,
	solarRecommendation: solarRecommendationReducer,
	buySolarProductDetails: buySolarProductDetailsReducer,
	guestDeliveryAddress: guestDelieveryAddressReducer,
	billsLayoutConfig: billsLayoutReducer,
	serviceType: serviceTypeReducer,
	customerEnergyProfile: energyProfileReducer,
	buyElectricity: currentBuyElectricityTransactionReducer,
	bulkPurchase: bulkPurchaseReducer,
	guestBillPayment: guestBillPaymentReducer,
	workflow: workflowReducer,
	getSolar: getSolarReducer,
	notifications: notificationReducer,
	broadcastNotification: broadcastNotificationReducer,
});

const rootReducer: typeof appReducer = (state, action) => {
	if (action.type === logOut.type || action.type === tokenExpired.type) {
		return appReducer(undefined, action);
	}
	return appReducer(state, action);
};

const loadStateFromSessionStorage = (): Partial<RootState> | undefined => {
	try {
		const serializedState = sessionStorage.getItem("reduxState");
		if (serializedState === null) return undefined; // No state found
		const parsed = JSON.parse(serializedState);
		return {
			user: parsed.user,
			customerEnergyProfile: parsed.customerEnergyProfile,
			buyElectricity: parsed.buyElectricity,
			bulkPurchase: parsed.bulkPurchase,
			guestBillPayment: parsed.guestBillPayment,
		};
	} catch (err) {
		console.error("Could not load state from session storage:", err);
		return undefined;
	}
};

const saveStateToSessionStorage = (state: RootState) => {
	try {
		const serializedState = JSON.stringify({
			user: state.user,
			customerEnergyProfile: state.customerEnergyProfile,
			buyElectricity: state.buyElectricity,
			bulkPurchase: state.bulkPurchase,
			guestBillPayment: state.guestBillPayment
		});
		sessionStorage.setItem("reduxState", serializedState);
	} catch (err) {
		console.error("Could not save state to session storage:", err);
	}
};

const persistedState: Partial<RootState> | undefined = loadStateFromSessionStorage();

export const store = configureStore({
	reducer: rootReducer,
	preloadedState: persistedState,
	middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(apiSlice.middleware),
});
let lastSavedUser: unknown = null;
let lastSavedEnergyProfile: unknown = null;
let lastSavedBuyElectricity: unknown = null;
let lastSavedBulkPurchase: unknown = null;
let lastSavedGuestBillPayment: unknown = null;

store.subscribe(() => {
	const state = store.getState();
	if (
		state.user === lastSavedUser &&
		state.customerEnergyProfile === lastSavedEnergyProfile &&
		state.buyElectricity === lastSavedBuyElectricity &&
		state.bulkPurchase === lastSavedBulkPurchase &&
		state.guestBillPayment === lastSavedGuestBillPayment
	) return;

	lastSavedUser = state.user;
	lastSavedEnergyProfile = state.customerEnergyProfile;
	lastSavedBuyElectricity = state.buyElectricity;
	lastSavedBulkPurchase = state.bulkPurchase;
	lastSavedGuestBillPayment = state.guestBillPayment;

	saveStateToSessionStorage(state);
});

// Infer the `RootState` and `AppDispatch` types from the store itself

export type AppDispatch = typeof store.dispatch;
